// LegendOverlay — dynamic legend bound to visible layers (US-875 · AC-6).
//
// Reads LEGEND_SECTIONS + `visibleLayers` from the layers context and shows ONLY
// the rows whose linked layers are currently on, so the legend always matches the
// map. Re-renders automatically when context state changes (no manual event wiring
// needed). "Served" is a pseudo-row with no toggle: it shows whenever any gap
// layer is visible (`servedFor`). Sits in the map area's bottom-left chrome.
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import { GAP_STYLE_RASTER, LEGEND_SECTIONS } from "../../config/layerConfig";
import { rasterRamps } from "../../theme/tokens";
import useLayers from "../../hooks/useLayers";

// An item is visible when any of its linked layers is on; "served" uses servedFor.
const isItemVisible = (item, visibleLayers) => {
  const ids = item.servedFor || item.linkedLayers || [];
  return ids.some((id) => visibleLayers[id]);
};

const LegendOverlay = () => {
  const { visibleLayers, gapStyle } = useLayers();
  const { tokens } = useTheme();
  const { shadows } = tokens;

  const sections = LEGEND_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => isItemVisible(item, visibleLayers)),
  })).filter((section) => section.items.length > 0);

  if (sections.length === 0) return null;

  return (
    <Box
      aria-label="Map legend"
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 0,
        p: 1.25,
        boxShadow: shadows.anchor,
        minWidth: 168,
        maxWidth: 224,
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 0.75,
        }}
      >
        Active Layers
      </Typography>
      <Divider sx={{ mb: 0.75 }} />
      <Stack spacing={0.5}>
        {sections.map((section) =>
          section.items.map((item) =>
            // US-920 (R-2/A-2): in Continuous mode these rows render the ramp
            // as a gradient bar with labeled extremes; in Cells mode they fall
            // back to the flat vector swatch (item.color).
            item.gradient && gapStyle === GAP_STYLE_RASTER ? (
              <Box key={item.id}>
                <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.1 }}>
                  {item.label}
                </Typography>
                <Box
                  data-testid={`legend-gradient-${item.id}`}
                  sx={{
                    height: 8,
                    mt: 0.25,
                    borderRadius: "4px",
                    background: `linear-gradient(90deg, ${rasterRamps[
                      item.gradient.ramp
                    ].join(", ")})`,
                  }}
                />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
                    {item.gradient.minLabel}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
                    {item.gradient.maxLabel}
                  </Typography>
                </Stack>
                {/* US-920 (R-11): China renders from household-based bands —
                    declare the unit switch so the ramp reads honestly there. */}
                {item.gradient.chinaNote && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontSize: 9,
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {item.gradient.chinaNote}
                  </Typography>
                )}
              </Box>
            ) : (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Box
                  sx={{
                    width: 11,
                    height: 11,
                    flexShrink: 0,
                    borderRadius: item.shape === "circle" ? "50%" : "2px",
                    bgcolor: item.color,
                    border:
                      item.border ??
                      (item.shape === "circle"
                        ? "1px solid rgba(0,0,0,0.2)"
                        : "none"),
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.1 }}>
                  {item.label}
                </Typography>
              </Stack>
            )
          )
        )}
      </Stack>
    </Box>
  );
};

export default LegendOverlay;
